import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, Loader2, Search, UserCheck } from "lucide-react";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import PlayerConsentModal from "./PlayerConsentModal";
import { ConsentStatusBadge, Tick, formatDate } from "./consentShared";

/**
 * Under-16 teams: who each player's parent is, what they agreed to, and the
 * history behind it. Workspace admins can export or delete a player's data
 * from the player's detail.
 */
const ConsentTab = ({ workspaceId, teamId, onChanged }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [openPlayerId, setOpenPlayerId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await betaAPI.getTeamConsent({ workspaceId, teamId });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load consent", err);
      showToast.error(t("team.consentTab.toasts.loadFailed"));
      setData({
        players: [],
        pending_sign_ups: 0,
        rejected_sign_ups: [],
        can_manage: false,
      });
    }
  }, [workspaceId, teamId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const players = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = data?.players ?? [];
    if (!q) return all;
    return all.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.guardian?.name.toLowerCase().includes(q) ||
        p.guardian?.email.toLowerCase().includes(q)
    );
  }, [data, query]);

  const counts = useMemo(() => {
    const c = { granted: 0, awaiting: 0, withdrawn: 0 };
    (data?.players ?? []).forEach((p) => {
      if (c[p.consent_status] !== undefined) c[p.consent_status] += 1;
    });
    return c;
  }, [data]);

  if (!data) {
    return (
      <div className="flex flex-1 justify-center py-16">
        <Loader2 size={24} className="animate-spin text-[#E07A3A]" />
      </div>
    );
  }

  // The club may not ask for research consent (older API: both).
  const purposes = data.purposes || ["club_tracking", "research"];

  return (
    <div className="mx-auto min-h-0 w-full max-w-[1440px] flex-1 overflow-y-auto px-6 pb-8 pt-5">
      {/* Summary */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-[#4B5563]">
          <span>
            <span className="font-semibold text-[#111827]">
              {data.players.length}
            </span>{" "}
            {t("team.consentTab.summary.players", {
              count: data.players.length,
            })}
          </span>
          <span>
            <span className="font-semibold text-[#047857]">
              {counts.granted}
            </span>{" "}
            {t("team.consentTab.summary.granted")}
          </span>
          <span>
            <span className="font-semibold text-[#92400E]">
              {counts.awaiting}
            </span>{" "}
            {t("team.consentTab.summary.awaiting")}
          </span>
          {counts.withdrawn > 0 && (
            <span>
              <span className="font-semibold text-[#B91C1C]">
                {counts.withdrawn}
              </span>{" "}
              {t("team.consentTab.summary.withdrawn")}
            </span>
          )}
          {data.pending_sign_ups > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF1E8] px-2.5 py-1 text-xs font-semibold text-[#C2410C]">
              <UserCheck size={13} />
              {t("team.consentTab.summary.pending", {
                count: data.pending_sign_ups,
              })}
            </span>
          )}
        </div>
        <label className="relative block w-full sm:w-72">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("team.consentTab.search")}
            aria-label={t("team.consentTab.search")}
            className="w-full rounded-lg border border-[#E5E7EB] bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#E07A3A]"
          />
        </label>
      </div>

      {/* Players */}
      <div className="overflow-hidden rounded-xl border border-[#EEF0F2] bg-white">
        <table className="hidden w-full text-left text-sm md:table">
          <thead className="border-b border-[#F1F2F4] bg-[#FAFAFB] text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
            <tr>
              <th className="px-5 py-3">
                {t("team.consentTab.columns.player")}
              </th>
              <th className="px-5 py-3">
                {t("team.consentTab.columns.parent")}
              </th>
              <th className="px-3 py-3 text-center">
                {t("team.consentTab.columns.clubTracking")}
              </th>
              {purposes.includes("research") && (
                <th className="px-3 py-3 text-center">
                  {t("team.consentTab.columns.research")}
                </th>
              )}
              <th className="px-5 py-3">
                {t("team.consentTab.columns.status")}
              </th>
              <th className="w-10 px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr
                key={p.id}
                onClick={() => setOpenPlayerId(p.id)}
                className="cursor-pointer border-b border-[#F3F4F6] transition-colors last:border-0 hover:bg-[#FAFAFB]"
              >
                <td className="px-5 py-3.5">
                  <p className="font-semibold text-[#111827]">{p.name}</p>
                  {p.date_of_birth && (
                    <p className="text-xs text-[#6B7280]">
                      {t("team.consentTab.born", {
                        date: formatDate(p.date_of_birth),
                      })}
                    </p>
                  )}
                </td>
                <td className="px-5 py-3.5">
                  {p.guardian ? (
                    <>
                      <p className="text-[#111827]">{p.guardian.name}</p>
                      <a
                        href={`mailto:${p.guardian.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-[#6B7280] hover:text-[#E07A3A]"
                      >
                        {p.guardian.email}
                      </a>
                    </>
                  ) : (
                    <span className="text-xs text-[#9CA3AF]">
                      {t("team.consentTab.noParent")}
                    </span>
                  )}
                </td>
                {purposes.map((k) => (
                  <td key={k} className="px-3 py-3.5 text-center">
                    {p.consents ? (
                      <Tick
                        on={p.consents[k]}
                        label={
                          p.consents[k]
                            ? t("team.consentTab.yes")
                            : t("team.consentTab.no")
                        }
                      />
                    ) : (
                      <span className="text-[#D1D5DB]">–</span>
                    )}
                  </td>
                ))}
                <td className="px-5 py-3.5">
                  <ConsentStatusBadge player={p} />
                </td>
                <td className="px-3 py-3.5 text-[#9CA3AF]">
                  <ChevronRight size={16} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Phones: one card per player */}
        <ul className="divide-y divide-[#F3F4F6] md:hidden">
          {players.map((p) => (
            <li key={p.id}>
              <button
                onClick={() => setOpenPlayerId(p.id)}
                className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[#111827]">{p.name}</p>
                  <p className="truncate text-xs text-[#6B7280]">
                    {p.guardian
                      ? `${p.guardian.name} · ${p.guardian.email}`
                      : t("team.consentTab.noParent")}
                  </p>
                </div>
                <ConsentStatusBadge player={p} />
                <ChevronRight size={16} className="text-[#9CA3AF]" />
              </button>
            </li>
          ))}
        </ul>

        {players.length === 0 && (
          <p className="px-5 py-10 text-center text-sm text-[#6B7280]">
            {query
              ? t("team.consentTab.noMatches")
              : t("team.consentTab.empty")}
          </p>
        )}
      </div>

      {/* Rejected sign-ups */}
      {data.rejected_sign_ups.length > 0 && (
        <details className="mt-5 rounded-xl border border-[#EEF0F2] bg-white">
          <summary className="cursor-pointer px-5 py-3.5 text-sm font-medium text-[#374151]">
            {t("team.consentTab.rejected.title", {
              count: data.rejected_sign_ups.length,
            })}
          </summary>
          <div className="border-t border-[#F3F4F6] px-5 py-3">
            <p className="mb-2 text-xs text-[#6B7280]">
              {t("team.consentTab.rejected.note")}
            </p>
            <ul className="space-y-1.5 text-sm text-[#374151]">
              {data.rejected_sign_ups.map((r, i) => (
                <li key={i}>
                  {t("team.consentTab.rejected.row", {
                    date: formatDate(r.decided_at),
                    name: r.decided_by,
                  })}
                </li>
              ))}
            </ul>
          </div>
        </details>
      )}

      <PlayerConsentModal
        show={!!openPlayerId}
        onClose={() => setOpenPlayerId(null)}
        workspaceId={workspaceId}
        teamId={teamId}
        playerId={openPlayerId}
        onDeleted={() => {
          setOpenPlayerId(null);
          load();
          onChanged?.();
        }}
      />
    </div>
  );
};

export default ConsentTab;
